import { reactive, markRaw } from 'vue'
import {
	setMessage,
	getMessages,
	getMyUser,
	getUser,
	getMembershipForUser,
	updateUser,
} from '@/store'
import { parseJSON } from '@/helpers/utils'
import { listenForActiveSpeaker, logMeetingEvent } from './common'
import { startCaptions, stopCaptions } from './videosession'

const store = reactive({
	bubble: {},
	messages: [],
	user: {},
	userPrefs: {
		video: false,
		audio: false,
		hd: true,
	},
	session: {},
	videosession: {},
	connectionIds: [],
	usersById: {},
	streamsById: {},
	membersByUserId: {},
	typinglist: [],
	connectionsByUserId: {},
	connectionsByStreamId: {},
	participantsByConnectionId: {},
	captions: {
		isActive: false,
		items: [],
		partialCaptionsByStreamId: {},
	},
})

export default store

// IMPORTANT SETTER METHODS:

export const updateSpeaking = (connectionId, activeStreamId, speaking) => {
	updateParticipant(connectionId, { speaking, activeStreamId })
}

export const updateParticipant = (connectionId, data = {}) => {
	const participant = getParticipant(connectionId) || {}
	const updatedParticipant = {
		...participant,
		...(typeof data === 'function' ? data(participant) : data),
	}
	const me = getMyParticipant()
	if (updatedParticipant.isPublishing || connectionId === me?.connection?.id) {
		updatedParticipant.id = 'publish'
	} else {
		updatedParticipant.id = connectionId
	}
	store.participantsByConnectionId[connectionId] = updatedParticipant
	EventBus.$emit('participantUpdate', updatedParticipant)
}

export const updateMessages = ({ message }) => {
	setMessage(message)
}

export const setConnection = (connection) => {
	if (!connection) return
	const { user_id, user } = parseJSON(connection.data)
	store.connectionsByUserId[user_id] = connection.connectionId
	if (!store.connectionIds.includes(connection.connectionId)) {
		store.connectionIds.push(connection.connectionId)
	}
	updateUser(user_id, user)
	updateParticipant(connection.connectionId, {
		connection,
		userId: user_id,
	})
}

export const setVideoSession = (videosession) => {
	if (!videosession) return
	const prev = store.videosession
	logMeetingEvent('videosession updated', videosession)
	store.videosession = videosession
	if (!videosession.captions_enabled) {
		setCaptionsIsActive(false)
	}
	EventBus.$emit('videosessionUpdated', videosession, prev)
}

export const setSession = (session) => {
	if (!session) return
	store.session = markRaw(session)
	// store.session = session
	const { connection } = session
	if (connection) {
		updateParticipant(connection.id, { connection })
	}
}

export const setStream = (stream) => {
	if (!stream) return
	const oldStream = getStream(stream.id)
	if (oldStream?.disabledReason) {
		if (!stream.disabledReason && stream.disabledReason !== null) {
			// disabledReason only comes through in a videoDisabled event so we need to persist
			// it across stream updates until it gets set to null by a videoEnabled event
			logMeetingEvent(
				'persisting disabledReason',
				stream.id,
				oldStream.disabledReason
			)
			Object.assign(stream, { disabledReason: oldStream.disabledReason })
		}
	}
	const metadata = parseJSON(stream.name) || {}
	const videoType = metadata.videoType || stream.videoType
	Object.assign(stream, {
		metadata,
		realVideoType: videoType,
		isScreenShare: videoType === 'screen',
	})
	store.streamsById = {
		...store.streamsById,
		[stream.id]: stream,
	}
	EventBus.$emit('streamUpdate', stream)
	if (stream.isScreenShare) {
		EventBus.$emit('screenStream', stream)
	}
	const { connectionId } = stream.connection
	const participant = getParticipant(connectionId)
	if (participant.hasOwnProperty('isPublishing')) return stream
	updateParticipant(connectionId, (p) => ({
		streams: {
			...(p.streams || {}),
			[videoType]: stream,
		},
	}))
	store.connectionsByStreamId[stream.id] = connectionId
	return stream
}

export const setSubscriber = (subscriber) => {
	if (!subscriber) return
	const { streamId } = subscriber.stream
	const connectionId = getConnectionIdByStreamId(streamId)
	updateParticipant(connectionId, {
		subscriber: {
			...subscriber,
			streamId,
		},
	})
	listenForActiveSpeaker(subscriber)
}

export const setPublisher = (publisher) => {
	if (!publisher) return
	const connectionId = getMyConnectionId()
	publisher.stream = setStream(publisher.stream)
	updateParticipant(connectionId, { publisher, isPublishing: true })
	EventBus.$emit('publisherAdded', publisher)
	logMeetingEvent('Publisher', 'publisher updated in store')
}

export const setScreenPublisher = (screenPublisher) => {
	if (!screenPublisher) return
	const connectionId = getMyConnectionId()
	screenPublisher.stream = setStream(screenPublisher.stream)
	updateParticipant(connectionId, { screenPublisher, isSharing: true })
}

export const setPublishing = (isPublishing) => {
	const connectionId = getMyConnectionId()
	updateParticipant(connectionId, { isPublishing })
}

export const setSharing = (isSharing) => {
	const connectionId = getMyConnectionId()
	updateParticipant(connectionId, { isSharing })
}

export const removeConnection = (connectionId) => {
	if (store.connectionIds.includes(connectionId)) {
		store.connectionIds.splice(store.connectionIds.indexOf(connectionId), 1)
	}

	delete store.participantsByConnectionId['connectionId']
	EventBus.$emit('participantUpdate')
}

export const removeStream = (connectionId, type) => {
	delete getParticipant(connectionId).streams[type]
}

export const removePublisher = () => {
	delete getMyParticipant().publisher
}

export const removeScreenPublisher = () => {
	delete getMyParticipant().screenPublisher
}

export const setUserPrefs = (prefs = {}) => {
	const prev = store.userPrefs
	store.userPrefs = { ...store.userPrefs, ...prefs }
	EventBus.$emit('userPrefsUpdated', store.userPrefs, prev)
}

export const setCaptionsIsActive = (isActive = false) => {
	logMeetingEvent('Captions', 'trying to set captions_enabled to', isActive)
	const prev = isCaptionsActive()
	if (prev === isActive) return
	store.captions.isActive = isActive
	EventBus.$emit('captionsActive', isActive)
	const promise = isActive ? startCaptions() : stopCaptions()
	promise
		.then(({ ok }) => {
			if (ok) {
				logMeetingEvent('Captions', 'enabled change success', { ok, isActive })
				if (!isActive) {
					clearCaptionItems()
				}
			} else {
				logMeetingEvent('Captions', 'enabled change error', { ok, isActive })
				EventBus.$emit('captionsActive', !isActive)
				store.captions.isActive = !isActive
			}
		})
		.catch((e) => {
			logMeetingEvent('Captions', 'enabled change error', e.message || e)
			console.error(e)
			EventBus.$emit('captionsActive', !isActive)
			store.captions.isActive = !isActive
		})
	return isActive
}

export const toggleCaptionsIsActive = () => {
	const isActive = isCaptionsActive()
	return setCaptionsIsActive(!isActive)
}

export const clearCaptionItems = () => {
	store.captions = {
		...(store.captions || {}),
		items: [],
		partialCaptionsByStreamId: {},
	}
	EventBus.$emit('captionUpdated')
	logMeetingEvent('Captions', 'cleared captions history')
}

export const addCaptionItem = (data) => {
	if (!store.captions.isActive) return
	if (!data.id) {
		data.id = store.captions.items.length
	}
	if (data.streamId && !data.userId) {
		let user = getUserByStreamId(data.streamId)
		if (!user && data.streamId === getPublisherStream()?.id) {
			user = getMyUser()
		}
		if (user) {
			Object.assign(data, {
				userId: user.id,
				userName: user.fullname,
			})
		}
	}
	store.captions.items.push(data)
	EventBus.$emit('captionAdded', data)
	return data
}

export const updateCaptionItem = (data) => {
	if (!store.captions.isActive) return
	const caption = store.captions.items.find((c) => c.id === data.id)
	if (!caption) {
		logMeetingEvent('Captions', 'could not update caption item', item)
		return null
	}
	Object.assign(caption, data)
	EventBus.$emit('captionUpdated', caption)
	return caption
}

// OPTIONAL GETTER METHODS:

export const getBubble = () => {
	return store.bubble
}

export const getCurrentUser = () => {
	return getMyUser()
}

export const getSession = () => {
	return store.session
}

export const getConnectionIds = () => {
	return store.connectionIds
}

export const getParticipant = (connectionId) => {
	return store.participantsByConnectionId[connectionId]
}

export const getStream = (streamId) => {
	return store.streamsById[streamId] || null
}

export const getParticipants = () => {
	return getConnectionIds().map(getParticipant)
}

export const getStreamingParticipants = () => {
	return getParticipants().filter(hasStream)
}

export const getWatchingParticipants = () => {
	return getParticipants().filter((p) => !hasStream(p))
}

export const getSpeakingParticipant = () => {
	return getStreamsForDisplay().find((p) => p.speaking)
}

export const getSharingParticipants = () => {
	return getStreamsForDisplay().filter(
		(p) => p.isSharing || p.stream?.isScreenShare
	)
}

export const getStreamsForDisplay = () => {
	return getStreamingParticipants()
		.reduce(
			(acc, { id, streams, isSharing, isPublishing, userId, speaking }) => {
				if (isPublishing || isSharing) {
					if (isPublishing) {
						// if we're publishing, create record for that
						const stream = getPublisherStream()
						acc.push({
							id,
							isPublishing,
							userId,
							stream,
							connectionId: id,
							speaking: speaking || null,
						})
					}
					if (isSharing) {
						// if we're screen sharing, create record for that
						const stream = getScreenPublisherStream()
						acc.push({ id: 'screen', isSharing, userId, stream })
					}
					return acc
				}
				// otherwise, create records for each available subscriber
				return acc.concat(
					Object.values(streams || {}).map((stream) => ({
						id: `${id}_${stream.id}`,
						stream,
						userId,
						speaking: speaking || null,
						connectionId: id,
						isSubscriber: true,
					}))
				)
			},
			[]
		)
		.sort((a, b) => {
			// move my stream to front, then sort by stream.creationTime
			if (a.id === 'publish') return -1
			if (b.id === 'publish') return 1
			if (a.stream && b.stream) {
				return a.stream.creationTime - b.stream.creationTime
			}
			return 0
		})
}

export const hasStream = (participant) => {
	if (!participant) return false
	const { streams, isPublishing, isSharing } = participant
	return isPublishing || isSharing || Object.keys(streams || {}).length > 0
}

export const getConnectionIdByUserId = (userId) => {
	return store.connectionsByUserId[userId]
}

export const getConnectionById = (connectionId) => {
	return getParticipant(connectionId).connection
}

export const getUserByStreamId = (streamId) => {
	if (!streamId) return
	return getUserByConnectionId(getConnectionIdByStreamId(streamId))
}

export const getConnectionIdByStreamId = (streamId) => {
	return store.connectionsByStreamId[streamId]
}

export const getParticipantByUserId = (userId) => {
	return getParticipant(getConnectionIdByUserId(userId))
}

export const getMemberByUserId = (userId) => {
	return getMembershipForUser(userId)
}

export const getUserById = (userId) => {
	return getUser(userId)
}

export const getUserByConnectionId = (connectionId) => {
	const { userId } = getParticipant(connectionId) || {}
	return getUserById(userId)
}

export const getMemberByConnectionId = (connectionId) => {
	const { userId } = getParticipant(connectionId) || {}
	return getMemberByUserId(userId)
}

export const getMyConnectionId = () => {
	const { connection } = getSession() || {}
	return connection ? connection.id : null
}

export const getMyParticipant = () => {
	return getParticipant(getMyConnectionId())
}

export const getMyConnection = () => {
	return (getMyParticipant() || {}).connection
}

export const getPublisher = () => {
	return (getMyParticipant() || {}).publisher
}

export const getSubscribers = () => {
	return getParticipants()
		.map((p) => p.subscriber)
		.filter((s) => s)
}

export const getPublisherStream = () => {
	const pub = getPublisher()
	return pub ? getStream(pub.streamId) : null
}

export const getScreenPublisher = () => {
	return (getMyParticipant() || {}).screenPublisher
}

export const getScreenPublisherStream = () => {
	return (getScreenPublisher() || {}).stream
}

export const getIsPublishing = () => {
	return (getMyParticipant() || {}).isPublishing
}

export const getIsSharing = () => {
	return (getMyParticipant() || {}).isSharing
}

export const getVideoSession = () => {
	return store.videosession
}

export const getHostUser = () => {
	const videosession = getVideoSession()
	return videosession ? videosession.host : null
}

export const getIsHostByUserId = (userId) => {
	const host = getHostUser()
	return host && host.id === userId
}

export const getIsCoHostByUserId = (userId) => {
	const member = getMemberByUserId(userId)
	return member?.role === 'owner'
}

export const getSubscriberByConnectionId = (connectionId) => {
	return getParticipant(connectionId).subscriber
}

export const hasConnection = () => {
	return (
		store.videosession.hasOwnProperty('id') &&
		store.session.hasOwnProperty('id')
	)
}

export const getUserPrefs = () => {
	return store.userPrefs
}

export const getUserMessages = () => {
	const videoSessionDate = Date.parse(
		getVideoSession().created_at.replaceAll('-', '/')
	)
	return getMessages().filter(
		(m) =>
			m.systemevent === null &&
			videoSessionDate < Date.parse(m.created_at.replaceAll('-', '/'))
	)
}

export const getHostParticipant = () => {
	const host = getHostUser()
	if (!host) return null
	return getParticipantByUserId(host.id)
}

export const getHostParticipants = () => {
	return getParticipants().filter(
		(p) => getIsHostByUserId(p.userId) || getIsCoHostByUserId(p.userId)
	)
}

export const hasSubscribers = () => {
	return getParticipants().some((p) => p.subscriber)
}

export const isMuted = () => {
	const stream = getPublisherStream() || {}
	return !stream || !stream.hasAudio
}

export const isOverLimit = () => {
	return getStreamingParticipants().length >= getVideoSession().maxstreams
	// debugging for overlimit related
	// return getStreamingParticipants().length >= 2
}

export const getAllUsers = () => {
	return Object.values(store.usersById)
}

export const isCaptionsActive = () => {
	return store.captions.isActive
}

export const getCaptions = () => {
	return store.captions.items.slice()
}
